import axios, { CreateAxiosDefaults } from 'axios';
import Config from '../config.json';
import { Query } from '@tanstack/react-query';

export const STATIC_STARTAPP_ID = 0;

export const govosBaseUrl =
  (window.location.hostname === 'localhost'
    ? process.env.REACT_APP_GOVOS_DESKTOP_URL
    : Config.REACT_APP_GOVOS_BASE_URL) || '';

const govosDesktopApiUrl = `${govosBaseUrl}/portal/desktop/0/api1`;
const govosStartAppApiUrl = `${govosBaseUrl}/portal/a3start`;

const buildPaginationQuery = (
  baseUrl: string,
  pageSize: number = 10,
  pageIndex: number = 0,
  search?: string,
  appIds?: number[]
) => {
  const params = new URLSearchParams();
  if (pageSize !== undefined) params.append('pageSize', String(pageSize));
  if (pageIndex !== undefined) params.append('pageIndex', String(pageIndex));
  if (search) params.append('search', search);
  if (appIds && appIds.length > 0) {
    params.append('appIds', String(appIds?.join(',')));
  }
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
};

/**
 * Some API Endpoints like /verfahren are indepent from an startApp and therefore do not need a startAppId in theory, but due to our current impelementation it is required.
 * Most of the endpoints can be called with the static startAppId or rather without an id to be indepent from an startApp and mandant friendly.
 */
export const GovosApiRoutes = {
  login: () => `${govosDesktopApiUrl}/login`,
  logout: () => `${govosDesktopApiUrl}/logout`,
  user: () => `${govosDesktopApiUrl}/user`,
  resetPassword: () => `${govosDesktopApiUrl}/passwd`,
  startApps: () => `${govosDesktopApiUrl}/startapps`,
  products: () => `${govosDesktopApiUrl}/products`,
  // online-dienst
  configuredOnlinedienste: (
    pageSize: number = 10,
    pageIndex: number = 0,
    search?: string,
    appIds?: number[]
  ) =>
    buildPaginationQuery(
      `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/online-dienst`,
      pageSize,
      pageIndex,
      search,
      appIds
    ),
  configuredOnlinediensteStatus: (
    pageSize: number = 10,
    pageIndex: number = 0,
    search?: string,
    appIds?: number[]
  ) =>
    buildPaginationQuery(
      `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/online-dienst-status`,
      pageSize,
      pageIndex,
      search,
      appIds
    ),
  configuredOnlineDienst: (onlineDienstId: string | number) =>
    `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/online-dienst/${onlineDienstId}`,

  // user groups
  userGroups: (startAppId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/user-groups`,

  // members
  members: (startAppId: string | number) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}/members`,
  addMember: (startAppId: string | number) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}/members`,
  updateMemberRole: (startAppId: string | number, memberId: string) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}/members/${memberId}`,
  removeMember: (startAppId: string | number, memberId: string) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}/members/${memberId}`,

  // zustaendige Stelle
  zustaendigeStelle: (
    startAppId: string | number,
    onlineDienstId?: string | number
  ) =>
    onlineDienstId
      ? `${govosStartAppApiUrl}/${startAppId}/API1ODM/zustaendige-stelle/${onlineDienstId}`
      : `${govosStartAppApiUrl}/${startAppId}/API1ODM/zustaendige-stelle`,
  deleteZustaendigeStelle: (startAppId: string | number) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}`,
  createStartApp: () => `${govosDesktopApiUrl}/startapps`,
  changeOwner: (startAppId: string | number) =>
    `${govosDesktopApiUrl}/startapps/${startAppId}/change-owner`,

  // marketplace
  marketplaceOnlinedienste: () =>
    `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/marketplace`,
  marketplaceOnlinediensteForStartApp: (startAppId: number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/marketplace`,

  // bezahldienst
  paymentServices: (startAppId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/bezahldienst`,
  paymentService: (startAppId: string | number, processId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/bezahldienst/${processId}`,

  // verarbeitungsverzeichnis
  verarbeitungsverzeichnis: (
    startAppId: string | number,
    processId: string | number
  ) => `${govosStartAppApiUrl}/${startAppId}/API1ODM/vv/${processId}`,

  // zustellwege
  deliveryServices: (startAppId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/zustellweg`,
  deliveryService: (startAppId: string | number, processId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/zustellweg/${processId}`,

  // auth
  authenticationService: (
    startAppId: string | number,
    processId: string | number
  ) => `${govosStartAppApiUrl}/${startAppId}/API1ODM/auth/${processId}`,

  // overwritable fields
  ueberschreibbareFelder: (
    startAppId: string | number,
    processId: string | number,
    feldId?: string
  ) =>
    feldId
      ? `${govosStartAppApiUrl}/${startAppId}/API1ODM/ueberschreibbare-felder/${processId}/${feldId}`
      : `${govosStartAppApiUrl}/${startAppId}/API1ODM/ueberschreibbare-felder/${processId}`,
  updateUeberschreibbareFelder: (
    startAppId: string | number,
    processId: string | number,
    feldId: string
  ) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/ueberschreibbare-felder/${processId}/${feldId}`,

  // online-dienst
  onlineServiceState: (processId: string | number) =>
    `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/online-dienst-state/${processId}`,
  resetDraft: (processId: string | number) =>
    `${govosStartAppApiUrl}/${STATIC_STARTAPP_ID}/API1ODM/online-dienst/${processId}/reset-draft`,

  // zufi
  zufiService: (
    startAppId: string | number,
    processId: string | number,
    type?: string,
    search?: string
  ) => {
    const baseUrl = `${govosStartAppApiUrl}/${startAppId}/API1ODM/zufi/${processId}`;
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (search) params.append('search', search);
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  },

  // template
  serviceTemplates: (startAppId: string | number, serviceType?: string) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/service-templates${serviceType ? `?serviceType=${serviceType}` : ''}`,

  // template
  serviceTemplate: (startAppId: string | number, templateId: string | number) =>
    `${govosStartAppApiUrl}/${startAppId}/API1ODM/service-templates/${templateId}`
};

export const invalidateConfiguredOnlinediensteCache =
  (onlineDienstId: string | number) => (query: Query) => {
    const variables = query.queryKey.at(0) as string;
    return (
      !!variables?.includes(`/API1ODM/online-dienst/${onlineDienstId}`) ||
      // !!variables?.includes(`/API1ODM/online-dienst?pageSize=`) ||
      !!variables?.includes(`/API1ODM/online-dienst-status?pageSize=`)
    );
  };

export const invalidatePaginationConfiguredOnlinediensteCache = (
  query: Query
) => {
  const variables = query.queryKey.at(0) as string;
  return (
    !!variables?.includes(`/API1ODM/online-dienst?pageSize=`) ||
    !!variables?.includes(`/API1ODM/online-dienst-status?pageSize=`)
  );
};

export const invalidateServiceTemplatesCache = () => (query: Query) => {
  const variables = query.queryKey.at(0) as string;
  return !!variables?.includes(`/API1ODM/service-templates`);
};

axios.defaults.baseURL = govosBaseUrl;

export const apiConfig: CreateAxiosDefaults = {
  baseURL: govosBaseUrl,
  withCredentials: true,
  headers: {
    ...axios.defaults.headers
  }
};

export const govosClient = axios.create(apiConfig);
